/*
Copyright© Celligence International, LLC - 2024
This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import Styles from '../../css/LoanInformation.module.css';
import BtnStyles from '../../css/OneTimePaymentPage.module.css';
import EnvelopeWithBox from '../EnvelopeWithBox';
import LoadingModal from '../modals/LoadingModal';
import ButtonBorder from '../ButtonBorder';
import ButtonBorderWrapper from '../ButtonBorderWrapper';
import { useAuthContext } from '../../contexts/AuthContext';
import { getEpochAsYYYYMMDD, getTimestampAsDate } from '../../common/UtilityFunctions';
import { useEscrowPaymentScheduleContext } from '../../contexts/EscrowPaymentScheduleContext';
import { NumericFormat } from 'react-number-format';

function EscrowPaymentForm() {
  const DAY_IN_SECONDS = 86400; //Make sure we don't display IMP5 records within 1 day of Epoch zero, since these values will represent origination snapshot values
  const { fullAccessSuperUser } = useAuthContext();
  const {
    loading,
    installments,
    loanImpounds,
    loanPayments,
    hasChanges,
    save,
    nextEscrowDayYYYYMMDD,
    currentEscrowPeriod,
    endCurrEscrowPeriod,
    cancel,
    loanImpoundTotals,
    updatePayment,
    isNewImpoundApplicable,
    nextEscrowInstallment,
    isShortagePaid,
    isPaymentEditable,
    isImpoundEditable,
    showRow,
    monthAfterLpi,
    hideTwoYearsPrior,
    setHideTwoYearsPrior,
    isNextEscrowPaymentEditable,
    origNextEscrowDayLoanPayment,
    updateImpound,
  } = useEscrowPaymentScheduleContext();

  return (
    <ButtonBorderWrapper>
      <EnvelopeWithBox blockName='Escrow Payment Schedule' disabled={!fullAccessSuperUser}>
        {loading && <LoadingModal open={true} />}
        <div className={Styles.loanInfoTable}>
          <div className={Styles.leftColumn}>
            <table className={Styles.loanInfoTableSection}>
              {/* Create the table for escrow period amounts and allow only authorized users to change the values */}

              <tbody>
                {
                  <tr>
                    <th scope='row'>Effective Date</th>
                    <th scope='row'>Impound</th>
                    <th scope='row'>Shortage or Surplus</th>
                    <th scope='row'>Escrow Payment</th>
                  </tr>
                }
                {loanImpoundTotals
                  .filter((imp) => imp.effectPmtDte > DAY_IN_SECONDS && showRow(imp.effectPmtDte))
                  .map((item, index) => (
                    <tr key={item.recId}>
                      <td>{getTimestampAsDate(item.effectPmtDte)}</td>

                      {loanImpounds && loanImpounds[getEpochAsYYYYMMDD(item.effectPmtDte)] && (
                        <td>
                          <NumericFormat
                            value={loanImpounds[getEpochAsYYYYMMDD(item.effectPmtDte)].mnthlyImpAmt}
                            className={Styles.narrowCellSpacing}
                            prefix={'$'}
                            decimalScale={2}
                            fixedDecimalScale
                            type='text'
                            defaultValue={0}
                            thousandsGroupStyle='thousand'
                            thousandSeparator=','
                            isAllowed={(values) => {
                              const { floatValue } = values;
                              return !isNaN(floatValue);
                            }}
                            disabled={!isImpoundEditable(item)}
                            onValueChange={(values, sourceInfo) => {
                              if (sourceInfo.source == 'event') {
                                // only change value on user event change
                                updateImpound(item.effectPmtDte, values.floatValue);
                              }
                            }}
                          />
                        </td>
                      )}

                      <td>
                        {loanPayments && loanPayments[getEpochAsYYYYMMDD(item.effectPmtDte)] && (
                          <NumericFormat
                            value={
                              loanPayments[getEpochAsYYYYMMDD(item.effectPmtDte)].escrowShortageMonthlyRepaymentAmt
                            }
                            className={Styles.narrowCellSpacing}
                            prefix={'$'}
                            decimalScale={2}
                            fixedDecimalScale
                            type='text'
                            thousandsGroupStyle='thousand'
                            thousandSeparator=','
                            isAllowed={(values) => {
                              const { floatValue } = values;
                              return !isNaN(floatValue);
                            }}
                            disabled={!isPaymentEditable(loanPayments[getEpochAsYYYYMMDD(item.effectPmtDte)])}
                            onValueChange={(values, sourceInfo) => {
                              if (sourceInfo.source == 'event') {
                                // only change value on user event change
                                updatePayment(
                                  loanPayments[getEpochAsYYYYMMDD(item.effectPmtDte)].dueDate,
                                  values.floatValue,
                                );
                              }
                            }}
                          />
                        )}
                      </td>

                      <td>
                        <NumericFormat
                          value={installments[getEpochAsYYYYMMDD(item.effectPmtDte)]?.ti}
                          className={Styles.narrowCellSpacing}
                          prefix={'$'}
                          decimalScale={2}
                          fixedDecimalScale
                          type='text'
                          thousandsGroupStyle='thousand'
                          thousandSeparator=','
                          disabled
                        />
                      </td>
                    </tr>
                  ))}

                <br />
              </tbody>
              {isNewImpoundApplicable() && monthAfterLpi && (
                <tbody>
                  <tr>
                    <th>New</th>
                  </tr>
                  <tr>
                    <th scope='row'>Effective Date</th>
                    <th scope='row'>Impound</th>
                    <th scope='row'>Shortage or Surplus</th>
                  </tr>
                  <tr>
                    <td>{monthAfterLpi ? getTimestampAsDate(monthAfterLpi.getTime() / 1000) : ''}</td>

                    {loanImpounds && loanImpounds[nextEscrowDayYYYYMMDD] && (
                      <td>
                        <NumericFormat
                          value={loanImpounds[nextEscrowDayYYYYMMDD]?.mnthlyImpAmt}
                          className={Styles.narrowCellSpacing}
                          prefix={'$'}
                          decimalScale={2}
                          fixedDecimalScale
                          type='text'
                          thousandsGroupStyle='thousand'
                          thousandSeparator=','
                          isAllowed={(values) => {
                            const { floatValue } = values;
                            return !isNaN(floatValue);
                          }}
                          disabled={!fullAccessSuperUser}
                          onValueChange={(values, sourceInfo) => {
                            if (sourceInfo.source == 'event') {
                              // only change value on user event change
                              updateImpound(monthAfterLpi.getTime() / 1000, values.floatValue);
                            }
                          }}
                        />
                      </td>
                    )}

                    {origNextEscrowDayLoanPayment && (
                      <td>
                        <NumericFormat
                          value={
                            loanPayments && loanPayments[nextEscrowDayYYYYMMDD]
                              ? loanPayments[nextEscrowDayYYYYMMDD].escrowShortageMonthlyRepaymentAmt
                              : origNextEscrowDayLoanPayment?.escrowShortageMonthlyRepaymentAmt
                          }
                          className={Styles.narrowCellSpacing}
                          prefix={'$'}
                          decimalScale={2}
                          fixedDecimalScale
                          type='text'
                          thousandsGroupStyle='thousand'
                          thousandSeparator=','
                          isAllowed={(values) => {
                            const { floatValue } = values;
                            return !isNaN(floatValue);
                          }}
                          disabled={!isNextEscrowPaymentEditable()}
                          onValueChange={(values, sourceInfo) => {
                            if (sourceInfo.source == 'event') {
                              // only change value on user event change
                              updatePayment(monthAfterLpi.getTime() / 1000, values.floatValue);
                            }
                          }}
                        />
                      </td>
                    )}
                  </tr>{' '}
                  <br />
                </tbody>
              )}
            </table>
          </div>
          <div className={Styles.rightColumn}>
            <table className={Styles.loanInfoTableSection}>
              <tbody>
                <tr>
                  <th>Current Escrow Period</th>
                </tr>
                <tr>
                  <th>Shortage Paid:</th>
                  <td>{isShortagePaid()}</td>
                </tr>
                <tr>
                  <th>Escrow Period Start Date:</th>
                  <td>
                    {currentEscrowPeriod == null || (currentEscrowPeriod && currentEscrowPeriod < DAY_IN_SECONDS)
                      ? 'N/A'
                      : getTimestampAsDate(currentEscrowPeriod)}
                  </td>
                </tr>
                <tr>
                  <th>Escrow Period Through:</th>
                  <td>
                    {endCurrEscrowPeriod == null ||
                    currentEscrowPeriod == null ||
                    (currentEscrowPeriod && currentEscrowPeriod < DAY_IN_SECONDS) ||
                    (endCurrEscrowPeriod && endCurrEscrowPeriod < DAY_IN_SECONDS)
                      ? 'N/A'
                      : getTimestampAsDate(endCurrEscrowPeriod / 1000)}
                  </td>
                </tr>
              </tbody>
            </table>
            <table className={Styles.loanInfoTableSection}>
              <tbody>
                <tr>
                  <th>Next Escrow Payment</th>
                </tr>
                <tr>
                  <th>Escrow Due:</th>
                  <td>$ {nextEscrowInstallment?.ti}</td>
                </tr>
              </tbody>
            </table>
            <FormControlLabel
              label='Hide 2+ Years Prior'
              disableTypography={true}
              control={
                <Checkbox
                  checked={hideTwoYearsPrior}
                  onChange={(e) => {
                    setHideTwoYearsPrior(e.target.checked);
                  }}
                />
              }
            />
          </div>
        </div>
      </EnvelopeWithBox>
      <ButtonBorder>
        <Button disabled={!hasChanges} variant='contained' className={BtnStyles.button} onClick={save}>
          Save
        </Button>
        <Button disabled={!hasChanges} variant='contained' className={BtnStyles.button} onClick={cancel}>
          Cancel
        </Button>
      </ButtonBorder>
    </ButtonBorderWrapper>
  );
}

export default EscrowPaymentForm;
